import React from 'react';
import SectionCard from '../controls/SectionCard';
import Slider from '../controls/Slider';
import Toggle from '../controls/Toggle';
import ColorPanel from '../../ColorPanel/ColorPanel';
import { normalizePaint, paintFromHex } from '../../../utils/paintUtils';

const ImageStyleSection = ({
  model,
  palette,
  onUsePaint,
  onRequestEyedropper,
  photoColors,
  disabled,
}) => {
  const opacity = model.mixed('opacity');
  const radius = model.mixed('radius');
  const shadowEnabled = model.mixed('shadow.enabled');
  const shadowX = model.mixed('shadow.x');
  const shadowY = model.mixed('shadow.y');
  const shadowBlur = model.mixed('shadow.blur');
  const shadowOpacity = model.mixed('shadow.opacity');
  const shadowColor = model.mixed('shadow.color');

  const toggleShadow = () => {
    const nextValue = shadowEnabled.kind === 'mixed'
      ? true
      : !shadowEnabled.value;
    model.applyToSelection({ shadow: { enabled: nextValue } }, 'immediate', 'shadow-toggle');
  };

  return (
    <SectionCard title="Style">
      <Slider
        label="Transparency"
        min={0}
        max={100}
        step={1}
        value={opacity.kind === 'mixed' ? 100 : Math.round((opacity.value ?? 1) * 100)}
        mixed={opacity.kind === 'mixed'}
        onChange={(value) => model.applyToSelection({ opacity: value / 100 }, 'commitOnRelease', 'opacity-image')}
        onCommit={() => model.commitPending('opacity-image')}
        disabled={disabled}
      />
      <Slider
        label="Corner radius"
        min={0}
        max={120}
        step={1}
        value={radius.kind === 'mixed' ? 0 : radius.value}
        mixed={radius.kind === 'mixed'}
        onChange={(value) => model.applyToSelection({ radius: value }, 'commitOnRelease', 'radius')}
        onCommit={() => model.commitPending('radius')}
        disabled={disabled}
      />
      <Toggle
        checked={Boolean(shadowEnabled.value)}
        indeterminate={shadowEnabled.kind === 'mixed'}
        onChange={toggleShadow}
        label="Shadow"
        disabled={disabled}
      />
  {shadowEnabled.value && (
        <>
          <Slider
            label="Shadow X"
            min={-50}
            max={50}
            step={1}
            value={shadowX.kind === 'mixed' ? 0 : shadowX.value}
            mixed={shadowX.kind === 'mixed'}
            onChange={(value) => model.applyToSelection({ shadow: { x: value } }, 'commitOnRelease', 'shadow-x')}
            onCommit={() => model.commitPending('shadow-x')}
            disabled={disabled}
          />
          <Slider
            label="Shadow Y"
            min={-50}
            max={50}
            step={1}
            value={shadowY.kind === 'mixed' ? 0 : shadowY.value}
            mixed={shadowY.kind === 'mixed'}
            onChange={(value) => model.applyToSelection({ shadow: { y: value } }, 'commitOnRelease', 'shadow-y')}
            onCommit={() => model.commitPending('shadow-y')}
            disabled={disabled}
          />
          <Slider
            label="Shadow blur"
            min={0}
            max={80}
            step={1}
            value={shadowBlur.kind === 'mixed' ? 20 : shadowBlur.value}
            mixed={shadowBlur.kind === 'mixed'}
            onChange={(value) => model.applyToSelection({ shadow: { blur: value } }, 'commitOnRelease', 'shadow-blur')}
            onCommit={() => model.commitPending('shadow-blur')}
            disabled={disabled}
          />
          <Slider
            label="Shadow opacity"
            min={0}
            max={100}
            step={1}
            value={shadowOpacity.kind === 'mixed' ? 40 : Math.round((shadowOpacity.value ?? 0.2) * 100)}
            mixed={shadowOpacity.kind === 'mixed'}
            onChange={(value) => model.applyToSelection({ shadow: { opacity: value / 100 } }, 'commitOnRelease', 'shadow-opacity')}
            onCommit={() => model.commitPending('shadow-opacity')}
            disabled={disabled}
          />
          <ColorPanel
            label="Shadow"
            value={shadowColor.value || '#000000'}
            mixed={shadowColor.kind === 'mixed'}
            allowGradient={false}
            documentColors={palette?.documentColors}
            recentColors={palette?.recentColors}
            recentGradients={palette?.recentGradients}
            photoColors={photoColors}
            onChange={(paint) => {
              const normalized = normalizePaint(paintFromHex(
                paint?.color || paint?.stops?.[0]?.color || '#000000',
                paint?.alpha ?? 100,
              ));
              model.applyToSelection({ shadow: { color: normalized.color } }, 'debounced', 'shadow-color');
            }}
            onCommit={(paint) => {
              const normalized = normalizePaint(paintFromHex(
                paint?.color || paint?.stops?.[0]?.color || '#000000',
                paint?.alpha ?? 100,
              ));
              model.applyToSelection({ shadow: { color: normalized.color } }, 'debounced', 'shadow-color');
            }}
            onUsePaint={onUsePaint}
            onRequestEyedropper={
              onRequestEyedropper
                ? () => onRequestEyedropper((paint) => {
                  const normalized = normalizePaint(paintFromHex(
                    paint?.color || paint?.stops?.[0]?.color || '#000000',
                    paint?.alpha ?? 100,
                  ));
                  model.applyToSelection({ shadow: { color: normalized.color } }, 'debounced', 'shadow-color');
                })
                : undefined
            }
            disabled={disabled}
          />
        </>
      )}
    </SectionCard>
  );
};

export default ImageStyleSection;
